import type { EChartsOption } from 'echarts';
import type { ParsedTable, ChartGeneratorSettings } from '../types';
import {
  toNumber,
  resolveSeriesColors,
  GeneratorError,
  getChartColorByIndex,
  tooltipPercentFormatter,
  CHART_LEGEND_TOP,
} from './helpers';

/**
 * Generates ECharts options for a Pie chart
 *
 * @param t - The parsed table data
 * @param title - The title for the chart
 * @param settings - Settings with selectedHeader for single column pie chart
 * @returns ECharts option configuration object for a pie chart
 * @throws {GeneratorError} If the table structure is unsuitable
 */
export function pieOptions(
  t: ParsedTable,
  title: string,
  settings?: ChartGeneratorSettings
): EChartsOption {
  const selectedHeader = settings?.selectedHeader

  if (!selectedHeader) {
    throw new GeneratorError(
      'Pie chart requires a `selectedHeader` in settings to choose the data column.'
    )
  }

  if (t.colCnt < 2 || t.rowCnt === 0) {
    throw new GeneratorError(
      'Pie chart requires at least 2 columns (category + data) and 1 data row.'
    )
  }

  const headerIndex = t.headers.findIndex((h) => h === selectedHeader)

  if (headerIndex === -1) {
    throw new GeneratorError(
      `Selected header "${selectedHeader}" not found in table headers.`
    )
  }

  const gradientMap = settings?.gradientMap

  // Extract data
  const pieData = t.rows
    .filter((row) => {
      if (settings?.selectedRows && settings.selectedRows.length > 0) {
        return settings.selectedRows.includes(row[0])
      }
      return true
    })
    .map((row, index) => {
      const name = row[0] || 'Unnamed';
      const value = headerIndex < row.length ? toNumber(row[headerIndex]) : null;

      // Look up gradient or color for this slice
      const key = name.toLowerCase();
      const gradient = gradientMap?.[key];
      const color = settings?.colorMap?.[key] || getChartColorByIndex(index);

      return {
        name,
        value,
        itemStyle: { color: gradient || color },
      }
    })
    .filter((item): item is { name: string; value: number; itemStyle: { color: string | object } } =>
      item.value !== null && !isNaN(item.value) && item.value > 0
    )

  if (pieData.length === 0) {
    throw new GeneratorError('No valid numeric data found for pie chart.')
  }

  return {
    title: [
      {
        text: title,
        left: 'left',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      // #1172 (Matt, 1 Sep): the pie ships no legend — it named the same
      // slices the slice labels named. The top-centre band instead names the
      // ONE column the pie shows — the selected period — matching the
      // single-entry legend bar/column now carry. (A real legend cannot do
      // this: ECharts only renders pie legend entries that match a data item
      // name.)
      //
      // #1201 keeps that arrangement: the period name stays here, the slice
      // names stay on the slices, and NO legend is restored — a legend would
      // collide with this very band. `hideColumnCaption` leaves it out where
      // the column names nothing a reader needs and a tall slice label would
      // sit under it (the Sky output share pie, Kai 20 Oct 2026).
      ...(settings?.hideColumnCaption
        ? []
        : [{
            text: selectedHeader,
            left: 'center',
            top: CHART_LEGEND_TOP,
            textStyle: { fontSize: 11, fontWeight: 'normal' as const, color: '#666' },
          }]),
    ],
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const value = params.value || 0
        const percent = params.percent || 0
        // #1201 (Sky, Sep): for percentage data the two figures this used to
        // print were THE SAME NUMBER — the cell value is already a share, so
        // Sky read "45.0% (45.0%)". One figure now, named: `Yes: 45.0%`.
        //
        // Frequency data is the one case where they genuinely differ (a raw
        // count and that count's share of the pie) and both are worth
        // knowing, so it keeps the pair.
        if (settings?.dataType === 'freq') {
          return `${params.name}: ${value.toLocaleString()} (${percent.toFixed(1)}%)`
        }
        return `${params.name}: ${tooltipPercentFormatter(value)}`
      },
    },
    series: [
      {
        name: selectedHeader,
        type: 'pie',
        radius: '60%',
        // Nudged below centre so the pie clears the top legend band the
        // other generators reserve via their grid.top — a pie has no grid
        // to reserve it with.
        center: ['50%', '58%'],
        data: pieData,
        // #1201: the slice label carries the name AND the percentage, on two
        // lines — `Yes` over `45.0%`. ECharts' pie default is `{b}` alone
        // (the name), which left the split itself readable only on hover.
        //
        // The issue asked for the names to come OFF the slices because "the
        // legend handles this". It does not: #1172 deleted the pie's legend
        // precisely BECAUSE the slices named themselves, and the top-centre
        // band above is the period name, not a legend. Followed literally
        // that instruction would leave a chart identified by colour alone —
        // worse than the redundancy being fixed. What Sky actually reported
        // was DUPLICATION: the same percentage printed twice, and names in
        // two places at once. With no legend the name on the slice is not a
        // duplicate; it is the only identification the chart has. So: name
        // once, percentage once, and the tooltip says the same thing.
        //
        // Two lines rather than "Yes 45.0%" because a pie label paints
        // OUTSIDE the slice on a leader line, where horizontal room runs out
        // first at dashboard-cell width; stacking keeps both halves legible.
        //
        // Percentage data prints its own value, which is the true column
        // percentage; `{d}` would be the slice's share of the drawn total and
        // silently differs whenever the pie shows a subset of the rows.
        // Frequency data has no percentage of its own, so there `{d}` IS the
        // percentage — and the raw count still shows in the tooltip. 1dp
        // either way, matching this chart's own tooltip exactly (the 0dp the
        // column labels use is there to line up with axis ticks; a pie has
        // none).
        label: {
          show: true,
          formatter: (params: any) =>
            settings?.dataType === 'freq'
              ? `${params.name}\n${(params.percent || 0).toFixed(1)}%`
              : `${params.name}\n${tooltipPercentFormatter(params.value)}`,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
    color: resolveSeriesColors(pieData.map(d => d.name), settings?.colorMap),
  }
}
